import {creates, elementFactory, hoistCmp} from '@xh/hoist/core';
import {panel} from '@xh/hoist/desktop/cmp/panel';
import {WorldHeatmapWidgetModel} from './WorldHeatmapWidgetModel';
import WorldMap from '../../../../cmp/worldMap/WorldMap';
import {div} from '@xh/hoist/cmp/layout';
import {dialog} from '@xh/hoist/kit/blueprint';
import {Icon} from '@xh/hoist/icon';
import {grid} from '@xh/hoist/cmp/grid';
import {fmtPercent, fmtPrice} from '@xh/hoist/format';
import './WorldHeatmapWidget.scss';
import getSymbolFromCurrency from 'currency-symbol-map';
import ReactCountryFlag from 'react-country-flag';
import {COUNTRY_ISO_CODES} from './countryIsoCodeMap';

const flagRenderer = elementFactory(ReactCountryFlag);

export const worldHeatmapWidget = hoistCmp.factory({
    model: creates(() => WorldHeatmapWidgetModel),
    render({model}) {
        return panel({
            className: 'world-heatmap',
            items: [worldMap(), configDialog()]
        });
    }
});

const worldMap = hoistCmp.factory<WorldHeatmapWidgetModel>(({model}) =>
    WorldMap({
        size: 500,
        type: 'select-single',
        mapColor: 'var(--wh-default-country-color)',
        hoverColor: 'var(--xh-button-active-bg)',
        strokeColor: 'var(--xh-text-color)',
        countryColors: model.countryColors,
        hints: true,
        hintBackgroundColor: null,
        hintTextColor: 'var(--xh-text-color)',
        hintPadding: '0px',
        hintBorder: 'var(--xh-border-solid)',
        hintBorderRadius: '0px',
        getTooltip: country => getTooltip(country, model)
    })
);

function getTooltip(country: string, model: WorldHeatmapWidgetModel) {
    const quote = model.getIndexQuoteByCountry(country);
    const currencySymbol = quote?.currency ? getSymbolFromCurrency(quote.currency) : null;
    const isSymbol = currencySymbol && currencySymbol.length == 1;

    return div(
        div({
            className: 'xh-treemap-tooltip__label',
            items: [
                COUNTRY_ISO_CODES[country]
                    ? flagRenderer({
                          countryCode: COUNTRY_ISO_CODES[country],
                          svg: true,
                          style: {marginRight: '6px', verticalAlign: 'middle', fontSize: '1.5em'}
                      })
                    : null,
                country
            ]
        }),
        div({
            className: 'xh-treemap-tooltip__row',
            item: [
                div({item: 'Index:'}),
                div({item: quote?.ticker}),
                div({
                    omit: !quote?.currency,
                    item: `(${quote?.currency})`,
                    className: 'world-heatmap__currency'
                })
            ]
        }),
        div({
            className: 'xh-treemap-tooltip__row',
            item: [
                div({item: 'Price:'}),
                div({
                    item: quote
                        ? fmtPrice(quote.price, {
                              precision: 2,
                              prefix: isSymbol ? currencySymbol : null
                          })
                        : null
                })
            ]
        }),
        div({
            className: 'xh-treemap-tooltip__row',
            item: [
                div({item: 'Change:'}),
                div({
                    item: quote ? fmtPrice(quote.priceChange, {precision: 2, colorSpec: true}) : null
                })
            ]
        }),
        div({
            className: 'xh-treemap-tooltip__row',
            item: [
                div({item: 'Change %:'}),
                div({
                    item: quote ? fmtPercent(quote.priceChangePct, {precision: 2, colorSpec: true}) : null
                })
            ]
        })
    );
}

export const configDialog = hoistCmp.factory<WorldHeatmapWidgetModel>(({model}) =>
    dialog({
        style: {
            width: 400,
            height: 600
        },
        isOpen: model.isConfigOpen,
        onClose: () => model.closeConfig(),
        title: 'Configure World Heatmap Widget',
        icon: Icon.globe(),
        item: panel({
            item: grid()
        })
    })
);
